import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UIElement from '@ckeditor/ckeditor5-engine/src/view/uielement';
import { toWidget } from '@ckeditor/ckeditor5-widget/src/utils';
import Widget from '@ckeditor/ckeditor5-widget/src/widget';
import React from 'react';
import ReactDOM from 'react-dom';
// eslint-disable-next-line import/no-internal-modules
import { renderToStaticMarkup } from 'react-dom/server';
import { ConditionalVariablesAsText } from 'components/molecules/variables/conditional-variables-as-text';
import { ConditionalVariablesForBackend } from 'components/molecules/variables/conditional-variables-for-backend';
import { RemoveConditionalVariable } from 'components/molecules/variables/remove-conditional-variable';
import styles from './variable.module.css';
import { VariablesUI } from './variables-ui';

export class ConditionalVariables extends Plugin {
  static get requires() {
    return [Widget, VariablesUI];
  }

  static get pluginName(): string {
    return 'ConditionalVariables';
  }

  private conditionalVariables(variableId, templateType, container) {
    ReactDOM.render(
      <ConditionalVariablesAsText
        variableId={variableId}
        templateType={templateType}
        editor={this.editor}
      />,
      container,
    );
  }

  // eslint-disable-next-line class-methods-use-this
  private conditionalVariablesForBackend(variableId, templateType, container) {
    const htmlString = renderToStaticMarkup(
      <ConditionalVariablesForBackend
        variableId={variableId}
        templateType={templateType}
      />,
    );

    container.innerHTML = htmlString;
  }

  // eslint-disable-next-line class-methods-use-this
  private removeConditionalVariable(variableId, templateType, container) {
    ReactDOM.render(
      <RemoveConditionalVariable
        variableId={variableId}
        templateType={templateType}
      />,
      container,
    );
  }

  init() {
    this.defineSchema();
    this.defineConverters();
    this.defineEventListeners();
  }

  private defineEventListeners() {
    const { model } = this.editor;
    const { removeConditionalVariable } = this;

    /**
     * Below event listener has been referred from
     * https://github.com/shibbirweb/ckeditor5-image-remove-event-callback-plugin
     */

    model.document.on('change:data', (event) => {
      const { differ } = event.source as any;

      if (differ.isEmpty) {
        return;
      }

      const changes = differ.getChanges({
        includeChangesInGraveyard: true,
      });

      if (changes.length === 0) {
        return;
      }

      let hasNoVariablesRemoved = true;

      for (let i = 0; i < changes.length; i += 1) {
        const change = changes[i];

        if (
          change &&
          change.type === 'remove' &&
          change.name === 'conditionalVariables'
        ) {
          hasNoVariablesRemoved = false;
          break;
        }
      }

      // if not variables remove stop execution
      if (hasNoVariablesRemoved) {
        return;
      }

      // get removed nodes
      const removedNodes = changes.filter((change) => change.type === 'insert');

      removedNodes.forEach((node) => {
        const removedNode = node.position.nodeAfter;

        const conditionalVariables = removedNode.getAttribute(
          'conditionalVariables',
        );
        const templateType = removedNode.getAttribute('templateType');

        removeConditionalVariable(
          conditionalVariables,
          templateType,
          document.createElement('div'),
        );
      });
    });
  }

  private defineSchema() {
    const { schema } = this.editor.model;

    schema.register('conditionalVariables', {
      allowWhere: '$block',
      isObject: true,
      allowAttributes: ['conditionalVariables', 'templateType'],
    });
  }

  private defineConverters() {
    const { conversion } = this.editor;
    const renderConditionalVariables = this.conditionalVariables.bind(this);

    const renderConditionalVariablesForBackend =
      this.conditionalVariablesForBackend.bind(this);

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: ['conditionalVariables'],
      },
      model: (viewElement, { writer: modelWriter }) => {
        const conditionalVariables = viewElement.getAttribute(
          'data-conditionalvariables',
        );

        const templateType = viewElement.getAttribute('data-templatetype');

        return modelWriter.createElement('conditionalVariables', {
          conditionalVariables,
          templateType,
        });
      },
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'conditionalVariables',
      view: (modelElement, { writer: viewWriter }) => {
        const conditionalVariables = modelElement.getAttribute(
          'conditionalVariables',
        );
        const templateType = modelElement.getAttribute('templateType');
        const conditionalVariablesView = viewWriter.createContainerElement(
          'span',
          {
            class: `conditionalVariables ${styles.variable}`,
            'data-cke-ignore-events': true,
          },
        );
        const reactWrapper = viewWriter.createUIElement(
          'span',
          {},
          function (this: UIElement, domDocument: any) {
            const domElement = this.toDomElement(domDocument);

            renderConditionalVariables(
              conditionalVariables,
              templateType,
              domElement,
            );

            return domElement;
          },
        );

        viewWriter.insert(
          viewWriter.createPositionAt(conditionalVariablesView, 0),
          reactWrapper,
        );

        return toWidget(conditionalVariablesView, viewWriter);
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'conditionalVariables',
      view: (modelItem, { writer: viewWriter }) => {
        const conditionalVariables = modelItem.getAttribute(
          'conditionalVariables',
        );
        const templateType = modelItem.getAttribute('templateType');

        const conditionalVariablesView = viewWriter.createContainerElement(
          'span',
          {
            class: 'conditionalVariables',
            'data-conditionalvariables': conditionalVariables,
            'data-templatetype': templateType,
          },
        );
        const ConditionalVariablesEl = viewWriter.createRawElement(
          'span',
          {
            class: `${styles.variable}`,
          },
          (domElement) => {
            renderConditionalVariablesForBackend(
              conditionalVariables,
              templateType,
              domElement,
            );

            return domElement;
          },
        );

        viewWriter.insert(
          viewWriter.createPositionAt(conditionalVariablesView, 0),
          ConditionalVariablesEl,
        );

        return conditionalVariablesView;
      },
    });
  }
}
